/**
 * Skills — todas las skills entrenadas del char + cola de entrenamiento.
 *
 * Layout (card-grid style, artifact 02):
 *
 *   1. Header + LastUpdated + Refresh
 *
 *   2. Hero — "Top skill" (mayor SP del char), gradient banner estilo
 *      artifact 02. Skill name grande (Space Grotesk) + level pill + SP
 *      big en mono + barra de progreso hacia el siguiente level.
 *
 *   3. Strip secundario — 4 StatTile:
 *        - Total SP
 *        - Unallocated SP
 *        - Skills entrenadas (count)
 *        - Queue ETA (countdown live a la próxima finish_date, o "—")
 *
 *   4. Filter bar — search input + sort dropdown + level pills (L5/L4/
 *      L3/L2/L1/All) + count + Export CSV.
 *
 *   5. Training Queue section — card con hasta 5 entries + countdown
 *      live ETA. Si la queue está vacía, mensaje contextual.
 *
 *   6. Grid de SkillCards agrupadas por trained_level (L5 / L4 / L3 /
 *      L2 / L1). Cada card: skill name (truncado) + level pill + SP
 *      mono + mini-bar (relativa a la max SP de la categoría) + rank.
 *
 *   7. Footnote.
 *
 * Persistencia (localStorage):
 *   - "skills.search" → query de búsqueda
 *   - "skills.sort"   → 'sp-desc' | 'sp-asc' | 'name' | 'level'
 *   - "skills.level"  → 'all' | '5' | '4' | '3' | '2' | '1'
 *
 * Caveats:
 *   - ESI devuelve skills del catálogo SDE sin entrenar (SP=0). Las
 *     filtramos — son ruido para esta pantalla y para el CSV.
 *   - Skill categories NO están en la respuesta — solo skill_id +
 *     name + level + SP. Por eso agrupamos por level (5..1), que es
 *     la dimensión EVE-natural y no requiere el SDE category map.
 *   - useNow(1000) hook estable en el page → un solo interval compartido
 *     para el countdown ETA y los mini-bars "ready in X" (si los
 *     agregamos después).
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { useAuth } from '../hooks/useAuth'
import { useNow } from '../hooks/useNow'
import { Pill, StatTile, EmptyState, type PillTone } from '../components/ui'

type SortKey = 'sp-desc' | 'sp-asc' | 'name' | 'level'
type LevelFilter = 'all' | '5' | '4' | '3' | '2' | '1'

type SkillRow = {
  skill_id: number
  name: string
  active_level: number
  trained_level: number
  sp: number
}

type QueueEntry = {
  skill_id: number
  name: string
  finished_level: number
  start_date: string | null
  finish_date: string | null
}

const LEVEL_TONE: Record<number, PillTone> = {
  5: 'gold',
  4: 'gold',
  3: 'info',
  2: 'muted',
  1: 'muted',
}

function readLS<T extends string>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  const v = window.localStorage.getItem(key)
  return (v as T) ?? fallback
}

function writeLS(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // localStorage lleno / deshabilitado — OK, no persistimos
  }
}

export function Skills() {
  const { data: me } = useAuth()

  // Persisted state.
  const [query, setQuery] = useState<string>(() =>
    readLS<string>('skills.search', ''),
  )
  const [sortKey, setSortKey] = useState<SortKey>(() =>
    readLS<SortKey>('skills.sort', 'sp-desc'),
  )
  const [levelFilter, setLevelFilter] = useState<LevelFilter>(() =>
    readLS<LevelFilter>('skills.level', 'all'),
  )

  useEffect(() => writeLS('skills.search', query), [query])
  useEffect(() => writeLS('skills.sort', sortKey), [sortKey])
  useEffect(() => writeLS('skills.level', levelFilter), [levelFilter])

  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['skills-list', forceRefetchKey],
    queryFn: () => api.skills(force),
  })
  // Mismo queryKey que usa TrainingQueueSection → React Query deduplica, una sola fetch.
  const { data: queueData } = useQuery({
    queryKey: ['skill-queue', forceRefetchKey],
    queryFn: () => api.skillQueue(force),
  })
  const queue: QueueEntry[] = queueData?.queue ?? []
  // useNow estable a nivel page — un solo interval compartido para todos
  // los countdowns de la queue.
  const now = useNow(1000)

  // Filtros + sort aplicados a las skills entrenadas (SP > 0).
  const filtered = useMemo(() => {
    if (!data?.skills) return []
    const trained = data.skills.filter((s) => s.sp > 0)
    const byLevel =
      levelFilter === 'all'
        ? trained
        : trained.filter((s) => s.trained_level === parseInt(levelFilter, 10))
    const q = query.trim().toLowerCase()
    const matched = q
      ? byLevel.filter((s) => s.name.toLowerCase().includes(q))
      : byLevel
    const sorted = [...matched]
    switch (sortKey) {
      case 'sp-desc':
        sorted.sort((a, b) => b.sp - a.sp)
        break
      case 'sp-asc':
        sorted.sort((a, b) => a.sp - b.sp)
        break
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'level':
        sorted.sort(
          (a, b) => b.trained_level - a.trained_level || b.sp - a.sp,
        )
        break
    }
    return sorted
  }, [data, query, sortKey, levelFilter])

  const totalSp = data?.total_sp ?? 0
  const unallocatedSp = data?.unallocated_sp ?? 0
  const trainedCount = (data?.skills ?? []).filter((s) => s.sp > 0).length
  const totalSkills = data?.skills.length ?? 0

  // Hero: skill con mayor SP (top trained). Si no hay data, null.
  const heroSkill = useMemo(() => {
    if (!data?.skills) return null
    const trained = data.skills.filter((s) => s.sp > 0)
    if (trained.length === 0) return null
    return [...trained].sort((a, b) => b.sp - a.sp)[0]
  }, [data])

  // Filtros aplicados a la cola (mismo search query).
  const filteredQueue = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? queue.filter((x) => x.name.toLowerCase().includes(q)) : queue
  }, [queue, query])

  // Próxima entry de la queue (la que termina primero). Usado por el StatTile "Queue ETA".
  const nextQueueEntry = useMemo(() => {
    const withFinish = queue.filter((q) => q.finish_date !== null)
    if (withFinish.length === 0) return null
    return [...withFinish].sort(
      (a, b) =>
        new Date(a.finish_date!).getTime() - new Date(b.finish_date!).getTime(),
    )[0]
  }, [queue])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Skills</h1>
          <p className="text-eve-muted text-sm">
            Todas tus skills entrenadas + cola de entrenamiento activa.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Hero — top skill */}
      {heroSkill && (
        <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-accent/30 rounded-md p-5 relative overflow-hidden">
          <div className="text-xs uppercase tracking-wider text-eve-muted">
            Top skill (by SP)
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-3xl sm:text-4xl font-semibold text-eve-text">
              {heroSkill.name}
            </span>
            <Pill tone={LEVEL_TONE[heroSkill.trained_level] ?? 'muted'} size="md">
              L{heroSkill.trained_level}
            </Pill>
            {heroSkill.active_level > heroSkill.trained_level && (
              <Pill tone="info" size="md">
                in training
              </Pill>
            )}
          </div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-2xl sm:text-3xl text-eve-accent font-bold">
              {heroSkill.sp.toLocaleString('en-US')}
            </span>
            <span className="text-sm text-eve-muted">SP</span>
            <span className="text-xs text-eve-muted-2">
              {((heroSkill.sp / Math.max(1, totalSp)) * 100).toFixed(1)}% del total
            </span>
          </div>
        </div>
      )}

      {/* Strip secundario */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          variant="muted"
          size="sm"
          tone="accent"
          label="SP total"
          value={totalSp.toLocaleString('en-US')}
          sub={`${((totalSp / 5_000_000) * 100).toFixed(0)}% de 5M (max char SP)`}
        />
        <StatTile
          variant="muted"
          size="sm"
          tone={unallocatedSp > 0 ? 'positive' : 'muted'}
          label="Sin asignar"
          value={unallocatedSp.toLocaleString('en-US')}
          sub={unallocatedSp > 0 ? 'inyectá a una skill' : 'todo asignado'}
        />
        <StatTile
          variant="muted"
          size="sm"
          label="Skills entrenadas"
          value={trainedCount.toLocaleString('en-US')}
          sub={`de ${totalSkills.toLocaleString('en-US')} totales`}
        />
        <StatTile
          variant="muted"
          size="sm"
          tone={nextQueueEntry ? 'accent' : 'muted'}
          label="Queue ETA"
          value={
            nextQueueEntry
              ? formatCountdown(new Date(nextQueueEntry.finish_date!).getTime() - now)
              : '—'
          }
          sub={
            nextQueueEntry
              ? `${nextQueueEntry.name} → L${nextQueueEntry.finished_level}`
              : queue.length > 0
                ? 'sin finish_date'
                : 'cola vacía'
          }
        />
      </div>

      {/* Filter bar */}
      <div className="bg-eve-panel border border-eve-border rounded-md p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar skill…"
            className="bg-eve-bg border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text placeholder:text-eve-muted focus:border-eve-accent focus:outline-none flex-1 min-w-[160px] md:max-w-xs"
          />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="bg-eve-bg border border-eve-border rounded-md px-2 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none"
            aria-label="Ordenar skills"
          >
            <option value="sp-desc">SP (mayor a menor)</option>
            <option value="sp-asc">SP (menor a mayor)</option>
            <option value="name">Nombre (A–Z)</option>
            <option value="level">Nivel entrenado</option>
          </select>
          <span className="text-eve-muted text-xs ml-auto">
            {filtered.length} / {trainedCount}
          </span>
          <button
            onClick={() =>
              exportSkillsCSV(
                filtered,
                filteredQueue,
                me?.character?.name ?? 'character',
              )
            }
            disabled={filtered.length === 0 && filteredQueue.length === 0}
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-eve-accent/40 text-eve-accent hover:bg-eve-accent/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Exporta skills entrenadas + cola de entrenamiento actual (respeta filtro y orden)"
          >
            ↓ Exportar CSV
          </button>
        </div>

        {/* Level filter pills */}
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <span className="text-[10px] uppercase tracking-wider text-eve-muted mr-1">
            Level:
          </span>
          <button
            type="button"
            onClick={() => setLevelFilter('all')}
            className="outline-none focus-visible:ring-1 focus-visible:ring-eve-accent/40 rounded-full"
          >
            <Pill tone={levelFilter === 'all' ? 'gold' : 'muted'} size="md">
              Todos · {trainedCount}
            </Pill>
          </button>
          {(['5', '4', '3', '2', '1'] as const).map((lv) => {
            const count = (data?.skills ?? [])
              .filter((s) => s.sp > 0 && s.trained_level === parseInt(lv, 10))
              .length
            const isActive = levelFilter === lv
            return (
              <button
                key={lv}
                type="button"
                onClick={() => setLevelFilter(lv)}
                className="outline-none focus-visible:ring-1 focus-visible:ring-eve-accent/40 rounded-full"
              >
                <Pill
                  tone={isActive ? (LEVEL_TONE[parseInt(lv, 10)] ?? 'muted') : 'muted'}
                  size="md"
                  dot={isActive}
                >
                  L{lv} · {count}
                </Pill>
              </button>
            )
          })}
        </div>
      </div>

      {/* Training Queue section */}
      <TrainingQueueSection queue={filteredQueue} now={now} />

      {/* Grid de skills agrupadas por level */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-display text-lg font-semibold text-eve-text">
            Skills
          </h2>
          <span className="text-[10px] text-eve-muted-2 italic">
            agrupadas por nivel · la más alta de cada grupo es la hero local
          </span>
        </div>

        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : !data?.skills?.length ? (
          <EmptyState
            title="Sin skills cargadas"
            description="Reintentá con el botón Refrescar — ESI puede haber rate-limiteado."
            variant="card"
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Sin resultados"
            description="Probá cambiando el filtro de level o limpiá la búsqueda."
            variant="card"
          />
        ) : (
          <SkillsGrid skills={filtered} />
        )}
      </div>

      <p className="text-xs text-eve-muted italic">
        ESI devuelve todas las skills del catálogo SDE (incluyendo las no
        entrenadas). Filtramos las de SP=0 para esta vista — son ruido para
        el CSV también. Agrupamos por <em>trained_level</em> (L5..L1) porque
        skill categories no vienen en la respuesta — es la dimensión EVE-
        natural y no requiere el SDE category map.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * TrainingQueueSection — card dedicado a la cola activa. Muestra hasta 5
 * entries con countdown live (useNow en el parent). Si la cola está vacía,
 * mensaje contextual con CTA.
 */
function TrainingQueueSection({
  queue,
  now,
}: {
  queue: QueueEntry[]
  now: number
}) {
  return (
    <section className="card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-baseline gap-2">
          <div className="stat-label">Cola de entrenamiento</div>
          {queue.length > 0 && (
            <Pill tone="gold" size="sm">
              {queue.length} {queue.length === 1 ? 'entry' : 'entries'}
            </Pill>
          )}
        </div>
      </div>

      {queue.length === 0 ? (
        <div className="text-eve-muted text-sm py-3">
          Cola vacía — agregá skills a la queue desde el cliente de EVE.
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            {queue.slice(0, 5).map((q) => {
              const isActive =
                q.start_date === null ||
                new Date(q.start_date).getTime() <= now
              const finishMs = q.finish_date
                ? new Date(q.finish_date).getTime()
                : null
              const remainingMs = finishMs ? finishMs - now : null
              return (
                <div
                  key={`${q.skill_id}-${q.finished_level}`}
                  className="flex items-baseline gap-2 flex-wrap text-sm py-1"
                >
                  <span className="text-eve-text flex-1 min-w-0 truncate">
                    {q.name}
                  </span>
                  <Pill
                    tone={isActive ? 'info' : 'muted'}
                    size="xs"
                    dot={isActive}
                  >
                    {isActive ? 'Training' : 'Queued'}
                  </Pill>
                  <span className="font-mono text-eve-text">→ L{q.finished_level}</span>
                  {remainingMs !== null && (
                    <span
                      className={`font-mono text-xs tabular-nums w-20 text-right ${
                        remainingMs > 0
                          ? 'text-eve-accent'
                          : 'text-eve-muted-2'
                      }`}
                    >
                      {remainingMs > 0
                        ? formatCountdown(remainingMs)
                        : 'done'}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
          {queue.length > 5 && (
            <div className="text-xs text-eve-muted mt-2">
              +{queue.length - 5} más en cola
            </div>
          )}
        </>
      )}
    </section>
  )
}

/**
 * SkillsGrid — agrupa skills por trained_level (5..1), renderiza cada
 * grupo como una sección con su propio grid de SkillCards. La primera
 * card de cada grupo (rank #1) recibe border accent + "★" para marcar
 * el top del grupo.
 */
function SkillsGrid({ skills }: { skills: SkillRow[] }) {
  // Agrupar por trained_level. Solo aparecen los levels presentes.
  const groups = useMemo(() => {
    const map = new Map<number, SkillRow[]>()
    for (const s of skills) {
      if (!map.has(s.trained_level)) map.set(s.trained_level, [])
      map.get(s.trained_level)!.push(s)
    }
    // Cada grupo viene ordenado por SP desc (viene del sort global).
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0])
  }, [skills])

  // Para bar relative, calculamos max SP por grupo (cada grupo se
  // escala contra sí mismo).
  const groupMaxSp = useMemo(() => {
    const m = new Map<number, number>()
    for (const [lv, list] of groups) {
      m.set(lv, Math.max(1, ...list.map((s) => s.sp)))
    }
    return m
  }, [groups])

  return (
    <div className="space-y-5">
      {groups.map(([level, list]) => {
        const maxSp = groupMaxSp.get(level) ?? 1
        const tone = LEVEL_TONE[level] ?? 'muted'
        return (
          <section key={level}>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-display text-sm font-semibold text-eve-text flex items-center gap-2">
                <Pill tone={tone} size="sm">
                  L{level}
                </Pill>
                <span className="text-eve-muted text-xs font-mono">
                  {list.length}{' '}
                  {list.length === 1 ? 'skill' : 'skills'}
                </span>
              </h3>
              <span className="text-[10px] text-eve-muted-2 italic">
                top: {list[0].sp.toLocaleString('en-US')} SP
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {list.map((s, i) => (
                <SkillCard
                  key={s.skill_id}
                  skill={s}
                  rank={i + 1}
                  isTop={i === 0}
                  maxSp={maxSp}
                />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

/**
 * SkillCard — una skill individual. Compacta (la mayoría de skills viven
 * en grids de 3-4 cols). La card top del grupo recibe border accent.
 */
function SkillCard({
  skill,
  rank,
  isTop,
  maxSp,
}: {
  skill: SkillRow
  rank: number
  isTop: boolean
  maxSp: number
}) {
  const barWidth = Math.max(2, (skill.sp / maxSp) * 100)
  const inTraining = skill.active_level > skill.trained_level
  return (
    <div
      className={`text-left rounded-md p-3 border transition-colors relative ${
        isTop
          ? 'border-eve-accent/40 bg-eve-panel'
          : 'border-eve-border bg-eve-panel hover:border-eve-accent/30'
      }`}
    >
      {isTop && (
        <span className="absolute top-1.5 right-1.5 text-[8px] font-bold text-eve-accent">
          ★
        </span>
      )}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div
          className="font-display font-medium text-sm text-eve-text truncate min-w-0"
          title={skill.name}
        >
          {skill.name}
        </div>
        <Pill
          tone={LEVEL_TONE[skill.trained_level] ?? 'muted'}
          size="xs"
        >
          L{skill.trained_level}
        </Pill>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-mono font-bold text-eve-text tabular-nums">
          {formatISKShort(skill.sp)}
        </span>
        <span className="text-[10px] text-eve-muted">SP</span>
        {inTraining && (
          <span className="text-[9px] uppercase tracking-wider text-eve-info ml-auto">
            in train
          </span>
        )}
        {!inTraining && (
          <span className="text-[9px] text-eve-muted-2 font-mono ml-auto">
            #{rank}
          </span>
        )}
      </div>
      <div className="mt-1.5 h-0.5 bg-eve-bg rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${
            isTop ? 'bg-eve-accent' : 'bg-eve-accent/50'
          }`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </div>
  )
}

/**
 * Genera un CSV combinando skills entrenadas + cola de entrenamiento
 * (respeta el filtro de texto y el orden actual) y dispara la descarga.
 *
 * Por qué CSV y no XLSX:
 *  - XLSX real necesita una lib (~80KB) que no queremos sumar al bundle.
 *  - Excel abre CSV nativamente (asistente de importación).
 *  - Las comas/quotado/newlines se escapan según RFC 4180.
 *
 * Columnas: Skill, Active Level, Trained Level, SP, Target Level, Status
 *  - "Trained"  → la skill no está en la cola
 *  - "Training" → la skill está en la cola y su start_date es pasado o null
 *  - "Queued"   → la skill está en la cola pero todavía no empezó
 *
 * Si una cola referencia una skill que no aparece en las entrenadas (caso
 * raro), se incluye igual con Active/Trained/SP vacíos y Status derivado.
 */
function exportSkillsCSV(
  trained: SkillRow[],
  queue: QueueEntry[],
  characterName: string,
): void {
  const escape = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v)
    // RFC 4180: quote si contiene comma, quote, CR o LF; escapar quotes duplicándolas.
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }

  // Por skill: la entrada de cola con finished_level más alto (= próxima meta).
  const queueById = new Map<number, QueueEntry>()
  for (const q of queue) {
    const prev = queueById.get(q.skill_id)
    if (!prev || q.finished_level > prev.finished_level) {
      queueById.set(q.skill_id, q)
    }
  }

  const now = Date.now()
  const statusFor = (q: QueueEntry | undefined): string => {
    if (!q) return 'Trained'
    if (q.start_date && new Date(q.start_date).getTime() <= now) return 'Training'
    return 'Queued'
  }

  const header = ['Skill', 'Active Level', 'Trained Level', 'SP', 'Target Level', 'Status']
  const lines: string[] = [header.map(escape).join(',')]

  const seen = new Set<number>()
  for (const r of trained) {
    seen.add(r.skill_id)
    const q = queueById.get(r.skill_id)
    lines.push([
      escape(r.name),
      r.active_level,
      r.trained_level,
      r.sp,
      q?.finished_level ?? '',
      statusFor(q),
    ].join(','))
  }

  // Skills en cola sin entrada en `trained` (defensivo: debería ser raro).
  for (const q of queue) {
    if (seen.has(q.skill_id)) continue
    lines.push([
      escape(q.name),
      '',
      '',
      '',
      q.finished_level,
      statusFor(q),
    ].join(','))
  }

  // BOM para que Excel detecte UTF-8 sin preguntar.
  const csv = '﻿' + lines.join('\r\n')

  const safeName = characterName.replace(/[^a-zA-Z0-9_-]/g, '_')
  const date = new Date().toISOString().slice(0, 10)
  const filename = `skills-${safeName}-${date}.csv`

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Liberar el object URL en el siguiente tick (no hace falta esperar al browser).
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * Format ms → "Xy Xd Xh Xm Xs". Usado por el StatTile "Queue ETA" y por
 * los countdowns en TrainingQueueSection.
 */
function formatCountdown(ms: number): string {
  if (ms <= 0) return 'done'
  const totalSec = Math.floor(ms / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}
